// AGC: Product Detection Modal - Camera-based product identification
// AGC: Replaces placeholder ScanInvoiceModal with full detection pipeline
// AGC: Phases: A (Capture) -> B (Processing) -> C (Results) -> D (Post-Detection Routing)
// AGC: Full-screen vertical camera with orientation toggle and frame size controls

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Camera, Loader2, Check, AlertTriangle, Search, Plus, Minus, ScanBarcode, RefreshCw, Smartphone, Monitor, Maximize2, Minimize2, Square, Zap, ChevronLeft } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { motion, AnimatePresence } from 'framer-motion'
import { checkImageQuality } from '../../utils/imageQualityCheck'
import { getBackendUrl } from '../../utils/backendUrl'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { isMediaPermissionError } from '../../services/permissionHelper'
import type {
  ProductDetectionResponse,
  DetectionCandidate,
  InventoryItem,
  ImageQualityResult,
} from '../../types/inventory'

// =============================================================================
// AGC: Types
// =============================================================================

export interface ScanInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  farmId?: string
  userId?: string
  inventoryItems?: InventoryItem[]
  onProductConfirmed?: (productId: string, quantity: number, isNew: boolean, prefillData?: Record<string, unknown>) => void
}

type ModalPhase = 'capture' | 'processing' | 'results' | 'add_stock'
type FrameOrientation = 'vertical' | 'horizontal'
type FrameSize = 'small' | 'medium' | 'large'

// AGC: Frame dimensions based on orientation and size
const FRAME_CONFIGS: Record<FrameOrientation, Record<FrameSize, { w: string; h: string }>> = {
  vertical: {
    small:  { w: 'w-36', h: 'h-52' },
    medium: { w: 'w-52', h: 'h-72' },
    large:  { w: 'w-64', h: 'h-[85%]' },
  },
  horizontal: {
    small:  { w: 'w-52', h: 'h-36' },
    medium: { w: 'w-72', h: 'h-48' },
    large:  { w: 'w-[90%]', h: 'h-56' },
  },
}

// =============================================================================
// AGC: Evidence tag component
// =============================================================================

function EvidenceTag({ source }: { source: string }) {
  const { t } = useTranslation()
  const config: Record<string, { color: string; label: string }> = {
    barcode: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', label: t('productDetection.matchedByBarcode', 'Barcode') },
    ocr: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: t('productDetection.matchedByOCR', 'Label text') },
    embedding: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: t('productDetection.matchedByEmbedding', 'Catalog match') },
    sota: { color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300', label: t('productDetection.matchedBySOTA', 'Smart Match') },
    vision: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', label: t('productDetection.matchedByVision', 'Visual match') },
    signature: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', label: t('productDetection.matchedBySignature', 'Saved signature') },
  }
  const { color, label } = config[source] || { color: 'bg-gray-100 text-gray-700', label: source }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
}

// =============================================================================
// AGC: Frame size icon button component
// =============================================================================

function FrameSizeButton({ size, active, onClick, icon }: { size: string; active: boolean; onClick: () => void; icon: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
        active
          ? 'bg-white/30 text-white ring-1 ring-white/50'
          : 'bg-black/20 text-white/70 hover:bg-white/20'
      }`}
      title={size}
    >
      {icon}
    </button>
  )
}

// =============================================================================
// AGC: Main component
// =============================================================================

export const ScanInvoiceModal: React.FC<ScanInvoiceModalProps> = ({
  isOpen,
  onClose,
  farmId = 'default',
  userId,
  inventoryItems = [],
  onProductConfirmed,
}) => {
  const { t, i18n } = useTranslation()

  // AGC: Phase state
  const [phase, setPhase] = useState<ModalPhase>('capture')
  const [processingStatus, setProcessingStatus] = useState('')

  // AGC: Camera state
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [cameraReady, setCameraReady] = useState(false)
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')

  // AGC: Frame controls
  const [frameOrientation, setFrameOrientation] = useState<FrameOrientation>('vertical')
  const [frameSize, setFrameSize] = useState<FrameSize>('medium')

  // AGC: Quality gate state
  const [qualityResult, setQualityResult] = useState<ImageQualityResult | null>(null)

  // AGC: Detection state
  const [detectionResult, setDetectionResult] = useState<ProductDetectionResponse | null>(null)
  const [selectedCandidate, setSelectedCandidate] = useState<DetectionCandidate | null>(null)
  const [detectedBarcode, setDetectedBarcode] = useState<string | null>(null)

  // AGC: Post-detection state (Phase D)
  const [addQuantity, setAddQuantity] = useState(1)
  const [existingItem, setExistingItem] = useState<InventoryItem | null>(null)
  const [entryPrice, setEntryPrice] = useState('')

  // AGC: Error state
  const [error, setError] = useState<string | null>(null)
  const [showCameraBlocked, setShowCameraBlocked] = useState(false)

  // ─── Camera lifecycle ────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
        setCameraReady(true)
      }
    } catch (err) {
      console.error('AGC: Camera access failed:', err)
      if (isMediaPermissionError(err)) {
        // AGC: Camera permission blocked -- show settings modal
        setShowCameraBlocked(true)
        setError(null)
      } else {
        setError(t('productDetection.cameraError', 'Could not access camera'))
      }
    }
  }, [facingMode, t])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCameraReady(false)
  }, [])

  useEffect(() => {
    if (isOpen && phase === 'capture') {
      startCamera()
    }
    return () => { stopCamera() }
  }, [isOpen, phase, startCamera, stopCamera])

  // ─── Reset on close ──────────────────────────────────────
  const handleClose = useCallback(() => {
    stopCamera()
    setPhase('capture')
    setDetectionResult(null)
    setSelectedCandidate(null)
    setDetectedBarcode(null)
    setQualityResult(null)
    setError(null)
    setAddQuantity(1)
    setExistingItem(null)
    setEntryPrice('')
    setProcessingStatus('')
    onClose()
  }, [onClose, stopCamera])

  // ─── Capture photo ───────────────────────────────────────
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)

    // AGC: Convert to blob for quality check
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.9)
    })

    // AGC: Quality gate -- advisory only, never blocks
    // AGC: Shows warnings but always proceeds with detection
    setProcessingStatus(t('productDetection.checkingQuality', 'Checking image quality...'))
    const quality = await checkImageQuality(blob)
    setQualityResult(quality)

    // AGC: Convert to base64 for upload
    const base64 = canvas.toDataURL('image/jpeg', 0.9)

    // AGC: Stop camera and start processing (always proceed)
    stopCamera()
    setPhase('processing')
    await runDetection(base64, detectedBarcode)
  }, [t, stopCamera, detectedBarcode])

  // ─── Run detection pipeline ──────────────────────────────
  const runDetection = async (imageBase64: string, barcode: string | null) => {
    try {
      setProcessingStatus(t('productDetection.scanning', 'Scanning barcode...'))
      await new Promise(r => setTimeout(r, 300))

      setProcessingStatus(t('productDetection.readingLabel', 'Reading label...'))
      await new Promise(r => setTimeout(r, 300))

      setProcessingStatus(t('productDetection.identifying', 'Identifying product...'))

      const backendUrl = getBackendUrl()
      const token = await (await import('firebase/auth')).getAuth().currentUser?.getIdToken()

      const response = await fetch(`${backendUrl}/api/v1/product-detection/detect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // AGC: Pass user locale so vision AI responds in the user's language
        body: JSON.stringify({ image: imageBase64, barcode, farmId, locale: i18n.language || 'pt' }),
      })

      if (!response.ok) {
        throw new Error(`Detection failed: ${response.status}`)
      }

      const result: ProductDetectionResponse = await response.json()
      setDetectionResult(result)
      setPhase('results')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Detection failed'
      console.error('AGC: Detection pipeline error:', err)
      setError(message)
      setPhase('results')
    }
  }

  // ─── Confirm a candidate ─────────────────────────────────
  const confirmCandidate = useCallback((candidate: DetectionCandidate) => {
    setSelectedCandidate(candidate)

    const found = inventoryItems.find(
      item => item.id === candidate.product_id ||
        (item.barcode && item.barcode === detectionResult?.extracted?.barcode) ||
        item.name.toLowerCase() === candidate.name.toLowerCase()
    )

    // AGC: Also check post-vision SOTA match (vision identified a name, SOTA matched it to inventory)
    const sotaMatch = detectionResult?.extracted?.sota_inventory_match
    const sotaFound = sotaMatch ? inventoryItems.find(item => item.id === sotaMatch.id) : null

    if (found) {
      setExistingItem(found)
      setPhase('add_stock')
    } else if (sotaFound && sotaMatch && sotaMatch.score >= 0.60) {
      // AGC: Post-vision SOTA found a matching inventory item — go to add stock
      setExistingItem(sotaFound)
      setPhase('add_stock')
    } else {
      // AGC: No match — pass prefill data to the parent's standard register modal
      const detected = detectionResult
      const visionName = detected?.best?.name || ''
      const extractedName = detected?.extracted?.product_name || ''
      const prefillData = {
        name: visionName || extractedName || candidate.name || '',
        category: detected?.extracted?.category || sotaMatch?.category || 'other',
        unit: detected?.extracted?.suggested_unit || sotaMatch?.unit || 'L',
        brand: detected?.extracted?.brand || '',
        barcode: detected?.extracted?.barcode || '',
        activeIngredient: detected?.extracted?.active_ingredient || '',
        initialQuantity: addQuantity,
      }
      onProductConfirmed?.('__new__', addQuantity, true, prefillData)
      handleClose()
    }
  }, [inventoryItems, detectionResult, addQuantity, onProductConfirmed, handleClose])

  // ─── Add stock to existing product ───────────────────────
  const handleAddStock = useCallback(async () => {
    if (!existingItem || !selectedCandidate) return

    try {
      const backendUrl = getBackendUrl()
      const token = await (await import('firebase/auth')).getAuth().currentUser?.getIdToken()
      await fetch(`${backendUrl}/api/v1/product-detection/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          productId: existingItem.id,
          farmId,
          barcode: detectionResult?.extracted?.barcode || null,
          ocrTextFingerprint: detectionResult?.extracted?.product_name || '',
          imageHash: Date.now().toString(),
          sourceConfidence: selectedCandidate.confidence,
        }),
      })
    } catch (err) {
      console.warn('AGC: Signature save failed:', err)
    }

    // AGC: Pass entry price as prefillData so parent can include costPerUnit in movement
    const priceValue = parseFloat(entryPrice) || 0
    onProductConfirmed?.(existingItem.id, addQuantity, false, priceValue > 0 ? { costPerUnit: priceValue } : undefined)
    handleClose()
  }, [existingItem, selectedCandidate, addQuantity, farmId, detectionResult, onProductConfirmed, handleClose, entryPrice])

  // ─── Retake photo ────────────────────────────────────────
  const handleRetake = useCallback(() => {
    setQualityResult(null)
    setPhase('capture')
    setError(null)
    setDetectionResult(null)
    setSelectedCandidate(null)
  }, [])

  // ─── Frame config ────────────────────────────────────────
  const currentFrame = FRAME_CONFIGS[frameOrientation][frameSize]

  if (!isOpen) return null

  return (
    <>
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={
          // AGC: Full-screen for capture/processing, standard modal for results/add_stock
          // AGC: z-[90] > BottomNav z-[80] + mic z-[85] — prevents nav from showing through camera
          phase === 'capture' || phase === 'processing'
            ? 'fixed inset-0 bg-black z-[90] flex flex-col'
            : 'fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center'
        }
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 400 }}
          className={
            phase === 'capture' || phase === 'processing'
              ? 'w-full h-full flex flex-col bg-black'
              : 'bg-white dark:bg-slate-800 w-full sm:w-[500px] sm:max-h-[85vh] max-h-[90vh] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col'
          }
          onClick={(e) => e.stopPropagation()}
        >
          {/* AGC: Content area */}
          <div className="flex-1 flex flex-col relative">

            {/* ── Phase A: Capture (full-screen camera) ──── */}
            {phase === 'capture' && (
              <>
                {/* AGC: Full-screen camera preview */}
                <div className="relative flex-1 bg-black overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <canvas ref={canvasRef} className="hidden" />

                  {/* AGC: Top bar overlay -- back + title + close - safe-area-top so it sits below the Android status bar */}
                  <div
                    className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-4 pb-8 bg-gradient-to-b from-black/60 to-transparent"
                    style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
                  >
                    <div className="flex items-center gap-2">
                      {/* AGC: Back arrow button — closes modal and returns to previous screen */}
                      <button
                        onClick={handleClose}
                        className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors"
                        aria-label={t('common.back', 'Voltar')}
                      >
                        <ChevronLeft className="w-5 h-5 text-white" />
                      </button>
                      <h2 className="text-white font-bold text-base flex items-center gap-2 drop-shadow-lg">
                        <ScanBarcode className="w-5 h-5" />
                        {t('productDetection.title', 'Detect Product')}
                      </h2>
                    </div>
                    <button onClick={handleClose} className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors">
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>

                  {/* AGC: Top-right controls -- orientation toggle + frame sizes */}
                  {cameraReady && (
                    <div className="absolute top-14 right-3 z-20 flex flex-col gap-2">
                      {/* AGC: Orientation toggle */}
                      <button
                        onClick={() => setFrameOrientation(o => o === 'vertical' ? 'horizontal' : 'vertical')}
                        className="w-9 h-9 rounded-lg bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors"
                        title={frameOrientation === 'vertical' ? 'Switch to horizontal' : 'Switch to vertical'}
                      >
                        {frameOrientation === 'vertical'
                          ? <Monitor className="w-4.5 h-4.5 text-white" />
                          : <Smartphone className="w-4.5 h-4.5 text-white" />
                        }
                      </button>

                      {/* AGC: Divider */}
                      <div className="w-full h-px bg-white/20" />

                      {/* AGC: Frame size: Small */}
                      <FrameSizeButton
                        size="S"
                        active={frameSize === 'small'}
                        onClick={() => setFrameSize('small')}
                        icon={<Minimize2 className="w-3.5 h-3.5" />}
                      />

                      {/* AGC: Frame size: Medium */}
                      <FrameSizeButton
                        size="M"
                        active={frameSize === 'medium'}
                        onClick={() => setFrameSize('medium')}
                        icon={<Square className="w-3.5 h-3.5" />}
                      />

                      {/* AGC: Frame size: Large */}
                      <FrameSizeButton
                        size="L"
                        active={frameSize === 'large'}
                        onClick={() => setFrameSize('large')}
                        icon={<Maximize2 className="w-3.5 h-3.5" />}
                      />
                    </div>
                  )}

                  {/* AGC: Scanning frame overlay -- adapts to orientation and size */}
                  {cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      {/* AGC: Dark vignette around scanning area */}
                      <div className="absolute inset-0 bg-black/30" />
                      {/* AGC: Scanning frame with corner markers */}
                      <div className={`relative ${currentFrame.w} ${currentFrame.h} bg-transparent rounded-lg z-10`}
                        style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }}
                      >
                        {/* AGC: Corner markers */}
                        <div className="absolute -top-0.5 -left-0.5 w-7 h-7 border-t-[3px] border-l-[3px] border-green-400 rounded-tl-lg" />
                        <div className="absolute -top-0.5 -right-0.5 w-7 h-7 border-t-[3px] border-r-[3px] border-green-400 rounded-tr-lg" />
                        <div className="absolute -bottom-0.5 -left-0.5 w-7 h-7 border-b-[3px] border-l-[3px] border-green-400 rounded-bl-lg" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-7 h-7 border-b-[3px] border-r-[3px] border-green-400 rounded-br-lg" />
                        {/* AGC: Scanning line animation */}
                        <div className="absolute inset-x-2 h-0.5 bg-green-400/60 animate-pulse" style={{ top: '50%' }} />
                      </div>
                    </div>
                  )}

                  {/* AGC: Quality gate warnings */}
                  {/* AGC: Quality warnings removed from camera phase since quality gate is now advisory-only */}
                </div>

                {/* AGC: Bottom capture bar - safe-area-bottom so capture button clears the Android nav bar */}
                <div
                  className="flex-shrink-0 pt-5 flex justify-center bg-black/90 backdrop-blur-sm"
                  style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.25rem)' }}
                >
                  <button
                    onClick={capturePhoto}
                    disabled={!cameraReady}
                    className="w-[72px] h-[72px] rounded-full bg-white/10 border-[3px] border-white flex items-center justify-center hover:bg-white/20 disabled:border-gray-500 disabled:opacity-50 transition-all active:scale-90"
                  >
                    <div className="w-[56px] h-[56px] rounded-full bg-white hover:bg-gray-100 disabled:bg-gray-600 flex items-center justify-center transition-colors">
                      <Camera className="w-7 h-7 text-gray-800" />
                    </div>
                  </button>
                </div>
              </>
            )}

            {/* ── Phase B: Processing ──────────────── */}
            {phase === 'processing' && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-6 bg-white dark:bg-slate-800">
                <div className="w-20 h-20 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-center font-medium">
                  {processingStatus}
                </p>
              </div>
            )}

            {/* ── Phase C: Results ─────────────────── */}
            {phase === 'results' && (
              <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-800">
                {/* AGC: Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <ScanBarcode className="w-5 h-5 text-purple-500" />
                    {t('productDetection.title', 'Detect Product')}
                  </h2>
                  <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {error ? (
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                      </div>
                      <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                      <button onClick={handleRetake} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700">
                        <RefreshCw className="w-4 h-4 inline mr-2" />
                        {t('productDetection.retake', 'Try Again')}
                      </button>
                    </div>
                  ) : detectionResult?.detected && detectionResult.best ? (
                    <>
                      {!detectionResult.needs_user_confirmation ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">{t('productDetection.highConfidence', 'Product detected!')}</span>
                          </div>
                          <button
                            onClick={() => confirmCandidate(detectionResult.best as unknown as DetectionCandidate)}
                            className="w-full p-4 border-2 border-green-500 rounded-xl bg-green-50 dark:bg-green-900/20 text-left"
                          >
                            <p className="font-bold text-gray-900 dark:text-white">{detectionResult.best.name}</p>
                            {detectionResult.best.variant && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{detectionResult.best.variant} • {detectionResult.best.size}</p>
                            )}
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {detectionResult.best.evidence.map((e, i) => (
                                <EvidenceTag key={i} source={e.source} />
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                              {Math.round(detectionResult.best.confidence * 100)}% {t('productDetection.confidence', 'confidence')}
                            </p>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="font-medium text-gray-700 dark:text-gray-300">
                            {t('productDetection.selectProduct', 'Select the correct product:')}
                          </p>
                          {detectionResult.candidates.map((candidate) => (
                            <button
                              key={candidate.product_id}
                              onClick={() => confirmCandidate(candidate)}
                              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 text-left transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-gray-900 dark:text-white">{candidate.name}</p>
                                <span className="text-xs text-gray-400">{Math.round(candidate.confidence * 100)}%</span>
                              </div>
                              {candidate.variant && (
                                <p className="text-sm text-gray-500">{candidate.variant} • {candidate.size}</p>
                              )}
                              <div className="flex gap-1 mt-1.5 flex-wrap">
                                {candidate.evidence.map((e, j) => (
                                  <EvidenceTag key={j} source={e.source} />
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      <button onClick={handleRetake} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        <RefreshCw className="w-4 h-4 inline mr-1" />
                        {t('productDetection.retake', 'Try Again')}
                      </button>
                    </>
                  ) : (
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Search className="w-8 h-8 text-amber-500" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 font-medium">
                        {t('productDetection.notFound', 'Could not identify product')}
                      </p>
                      {/* AGC: Show what the AI saw even when no catalog match */}
                      {detectionResult?.extracted?.product_name && (
                        <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-xl text-left">
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                            {t('productDetection.aiDetected', 'AI detected')}
                          </p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {detectionResult.extracted.product_name}
                          </p>
                          {detectionResult.extracted.category && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t('productDetection.category', 'Category')}: {t(`inventory.categories.${detectionResult.extracted.category}`, detectionResult.extracted.category)}
                            </p>
                          )}
                          {detectionResult.extracted.brand && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t('productDetection.brand', 'Brand')}: {detectionResult.extracted.brand}
                            </p>
                          )}
                        </div>
                      )}
                      {/* AGC: Show SOTA inventory match if found */}
                      {detectionResult?.extracted?.sota_inventory_match && (
                        <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl text-left">
                          <p className="text-xs text-cyan-600 dark:text-cyan-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {t('productDetection.matchedBySOTA', 'Smart Match')}
                          </p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t('productDetection.sotaFoundMatch', 'Similar item found in inventory')}: <strong>{detectionResult.extracted.sota_inventory_match.name}</strong>
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t('productDetection.confidence', 'confidence')}: {Math.round(detectionResult.extracted.sota_inventory_match.score * 100)}%
                          </p>
                          <button
                            onClick={() => {
                              // AGC: SOTA found existing item — go to add stock flow
                              const sotaItem = inventoryItems.find(item => item.id === detectionResult.extracted?.sota_inventory_match?.id)
                              if (sotaItem) {
                                setExistingItem(sotaItem)
                                setPhase('add_stock')
                              }
                            }}
                            className="mt-2 w-full py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            {t('productDetection.addToExisting', 'Add to this item')}
                          </button>
                        </div>
                      )}
                      {!detectionResult?.extracted?.product_name && !detectionResult?.extracted?.sota_inventory_match && (
                        <p className="text-sm text-gray-400">
                          {t('productDetection.noTextDetected', 'No product label or text detected in the image')}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button onClick={handleRetake} className="flex-1 py-2 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium">
                          {t('productDetection.retake', 'Try Again')}
                        </button>
                        <button
                          onClick={() => {
                            // AGC: Pass detection data to parent's standard register modal
                            const detected = detectionResult
                            const sotaMatch = detected?.extracted?.sota_inventory_match
                            const prefillData = {
                              name: detected?.best?.name || detected?.extracted?.product_name || '',
                              category: detected?.extracted?.category || sotaMatch?.category || 'other',
                              unit: detected?.extracted?.suggested_unit || sotaMatch?.unit || 'L',
                              brand: detected?.extracted?.brand || '',
                              barcode: detected?.extracted?.barcode || '',
                              activeIngredient: detected?.extracted?.active_ingredient || '',
                              initialQuantity: 0,
                            }
                            onProductConfirmed?.('__new__', 0, true, prefillData)
                            handleClose()
                          }}
                          className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg font-medium"
                        >
                          {t('productDetection.createManual', 'Add Manually')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Phase D: Add Stock (product exists) ── */}
            {phase === 'add_stock' && existingItem && (
              <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-800">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    {t('productDetection.productExists', 'Product found in inventory')}
                  </h2>
                  <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
                    <p className="font-bold text-gray-900 dark:text-white">{existingItem.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('productDetection.currentStock', 'Current stock')}: {existingItem.quantity} {existingItem.unit}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('productDetection.addQuantity', 'Quantity to add')}
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setAddQuantity(q => Math.max(1, q - 1))}
                        className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={addQuantity}
                        onChange={(e) => setAddQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 text-center text-xl font-bold border border-gray-300 dark:border-gray-600 rounded-lg py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={() => setAddQuantity(q => q + 1)}
                        className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-gray-500 dark:text-gray-400 font-medium">{existingItem.unit}</span>
                    </div>
                  </div>

                  {/* AGC: Optional price per unit for this entry */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('productDetection.entryPrice', 'Price per unit (optional)')}
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                      placeholder="R$ 0,00"
                      className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm"
                    />
                  </div>

                  <button
                    onClick={handleAddStock}
                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-colors"
                  >
                    {t('productDetection.addStock', 'Add Stock')}
                  </button>

                  <button onClick={handleRetake} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700">
                    {t('productDetection.retake', 'Try Again')}
                  </button>
                </div>
              </div>
            )}

            {/* AGC: create_product phase removed — now uses the unified "Cadastrar novo produto" modal from UnifiedInventoryPage */}

          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>

    {/* AGC: Camera permission blocked modal */}
    <PermissionBlockedModal
      isOpen={showCameraBlocked}
      permissionType="camera"
      onClose={() => {
        setShowCameraBlocked(false)
        onClose()
      }}
    />
    </>
  )
}

export default ScanInvoiceModal
